import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

/* ─────────────────────────────────────────────────────────────────────────────
   UI primitives — now theme-aware.

   Every color/border/shadow reads from a CSS variable set by ThemeContext on
   <html class="dark|light">. Dark-mode visuals are byte-identical to before;
   light mode gets WCAG-safe overrides via the `:root.light` rules in index.css.

   Tailwind palette classes (bg-slate-800, text-white, etc.) have been replaced
   with inline styles binding to tokens, OR with utility classes that are
   already themed in index.css (.input-premium, .badge-*, .custom-scroll).
   ───────────────────────────────────────────────────────────────────────────── */

/* ═══════════════════════════════════════════════════════════════════════════
   SearchableSelect
   Props unchanged.
   ═══════════════════════════════════════════════════════════════════════════ */
export const SearchableSelect = ({
  label, value, onChange, options = [],
  placeholder = '— Select —', disabled = false, required = false, className = '',
}) => {
  const [open,  setOpen]  = useState(false);
  const [query, setQuery] = useState('');
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 200 });
  const wrapRef  = useRef(null);
  const dropRef  = useRef(null);
  const inputRef = useRef(null);

  // Click-outside → close
  useEffect(() => {
    const handler = (e) => {
      const inWrap = wrapRef.current && wrapRef.current.contains(e.target);
      const inDrop = dropRef.current && dropRef.current.contains(e.target);
      if (!inWrap && !inDrop) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Scroll → close only if the trigger button itself scrolled off-screen
  // (don't close when user is scrolling inside the options list)
  useEffect(() => {
    if (!open) return;
    const onScroll = () => {
      if (!wrapRef.current) return;
      const r = wrapRef.current.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('scroll', onScroll, true);
    return () => document.removeEventListener('scroll', onScroll, true);
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const openDropdown = () => {
    if (disabled) return;
    if (!open && wrapRef.current) {
      const r = wrapRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      const dropHeight = Math.min(320, options.length * 44 + 80);
      const openAbove  = spaceBelow < dropHeight && spaceAbove > spaceBelow;
      setDropPos({
        left:  r.left,
        width: r.width,
        top:    openAbove ? undefined : r.bottom + 4,
        bottom: openAbove ? window.innerHeight - r.top + 4 : undefined,
      });
    }
    setOpen(o => !o);
  };

  const filtered = query.trim()
    ? options.filter(o =>
        o.label.toLowerCase().includes(query.toLowerCase()) ||
        (o.sub && o.sub.toLowerCase().includes(query.toLowerCase()))
      )
    : options;

  const selected = options.find(o => o.value === value);

  const pick = useCallback((opt) => {
    onChange(opt.value, opt);
    setOpen(false);
    setQuery('');
  }, [onChange]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); setQuery(''); }
    if (e.key === 'Enter' && filtered.length > 0) { e.preventDefault(); pick(filtered[0]); }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      {label && (
        <label
          className="block text-sm font-medium mb-1.5"
          style={{ color: 'var(--text-secondary)' }}
        >
          {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
      )}

      {/* Trigger */}
      <div
        onClick={openDropdown}
        className={`w-full rounded-lg text-sm transition-all cursor-pointer select-none
                    ${disabled ? 'opacity-40 cursor-not-allowed pointer-events-none' : ''}`}
        style={{
          padding: '9px 12px',
          background: 'var(--input-bg)',
          color: 'var(--text-primary)',
          border: `1px solid ${open ? 'var(--brand-primary)' : 'var(--border-subtle)'}`,
          boxShadow: open ? '0 0 0 3px rgba(99,102,241,0.15)' : 'none',
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="flex-1 min-w-0 truncate" style={{ color: selected ? 'var(--text-primary)' : 'var(--text-faint)' }}>
            {selected ? selected.label : placeholder}
          </span>
          <svg
            className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            style={{ color: 'var(--text-tertiary)' }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        {selected?.sub && (
          <span className="text-xs mt-0.5 block truncate" style={{ color: 'var(--text-tertiary)' }}>
            {selected.sub}
          </span>
        )}
      </div>

      {/* Dropdown — portal into body to escape backdropFilter stacking context */}
      {open && createPortal(
        <div
          ref={dropRef}
          className="rounded-xl custom-scroll"
          style={{
            position: 'fixed',
            top:       dropPos.top,
            bottom:    dropPos.bottom,
            left:      dropPos.left,
            width:     dropPos.width,
            minWidth:  `min(280px, calc(100vw - 16px))`,
            maxWidth:  `calc(100vw - 8px)`,
            maxHeight: 360,
            overflowY: 'auto',        /* single scroll container — no nested overflow */
            overflowX: 'hidden',      /* clip wide option labels instead of showing horizontal scroll */
            /* Keep centered rows centered even when the vertical scrollbar
               appears: reserving an equal gutter on both edges stops the
               scrollbar from stealing width only from the right. (Polish — the
               main left-shift bug was the search input; see minWidth:0 below.) */
            scrollbarGutter: 'stable both-edges',
            zIndex: 100000,
            background: 'var(--surface-1)',
            border: '1px solid var(--border-brand)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
          }}
        >
          {/* Search input — sticky so it stays visible while scrolling */}
          <div className="p-2" style={{
            position: 'sticky', top: 0, zIndex: 1,
            background: 'var(--surface-1)',
            borderBottom: '1px solid var(--border-subtle)',
          }}>
            <div
              className="flex items-center gap-2 rounded-lg px-3 py-1.5 focus-within:border-[var(--brand-primary)]"
              style={{ background: 'var(--input-bg)', border: '1px solid var(--border-subtle)', minWidth: 0 }}
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"
                strokeWidth={2} style={{ color: 'var(--text-tertiary)' }}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Type to search…"
                className="flex-1 bg-transparent text-sm focus:outline-none"
                /* minWidth:0 lets this flex item shrink below the <input>'s
                   intrinsic (~size=20) width. Without it the search row
                   overflows the narrow dropdown horizontally; auto-focusing the
                   input on open then scrolls the panel sideways, dragging every
                   centered row (header, options, footer) left. THIS was the real
                   "content shifted left" bug. */
                style={{ color: 'var(--text-primary)', minWidth: 0 }}
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="transition-colors"
                  style={{ color: 'var(--text-faint)' }}
                  onMouseEnter={e => (e.currentTarget.style.color = 'var(--text-secondary)')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-faint)')}
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Options list — no scroll here, scroll is on the outer portal div */}
          <div>
            {!required && (
              <div
                onClick={() => pick({ value: '', label: placeholder })}
                className="px-3 py-2 text-sm cursor-pointer transition-colors text-center"
                style={{
                  color: value === '' ? 'var(--accent-text)' : 'var(--text-secondary)',
                  background: value === '' ? 'var(--accent-bg)' : 'transparent',
                  borderBottom: '1px solid var(--border-subtle)',
                  letterSpacing: '0.01em',
                }}
                onMouseEnter={e => {
                  if (value !== '') {
                    e.currentTarget.style.background = 'var(--accent-bg-soft)';
                    e.currentTarget.style.color = 'var(--text-primary)';
                  }
                }}
                onMouseLeave={e => {
                  if (value !== '') {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'var(--text-secondary)';
                  }
                }}
              >
                — {placeholder} —
              </div>
            )}

            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-sm" style={{ color: 'var(--text-faint)', textAlign: 'center' }}>
                No results for "{query}"
              </div>
            ) : (
              filtered.map(opt => {
                const isSel = opt.value === value;
                return (
                  <div
                    key={opt.value}
                    onClick={() => pick(opt)}
                    className="px-4 py-2.5 text-sm cursor-pointer transition-colors"
                    style={{
                      // Flexbox column with centered items is the most reliable
                      // way to centre BOTH the main label and the optional
                      // sub-label, on both short and long strings. Plain
                      // text-align centre was failing for long labels in some
                      // browsers — likely because the row's effective content
                      // width matched the label width, leaving no room to
                      // visually centre.
                      display:        'flex',
                      flexDirection:  'column',
                      alignItems:     'center',
                      justifyContent: 'center',
                      textAlign:      'center',
                      color: isSel ? 'var(--accent-text)' : 'var(--text-secondary)',
                      background: isSel ? 'var(--accent-bg)' : 'transparent',
                      fontWeight: isSel ? 500 : 400,
                      wordBreak: 'break-word',
                      lineHeight: 1.3,
                    }}
                    onMouseEnter={e => {
                      if (!isSel) {
                        e.currentTarget.style.background = 'var(--accent-bg-soft)';
                        e.currentTarget.style.color = 'var(--text-primary)';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isSel) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = 'var(--text-secondary)';
                      }
                    }}
                  >
                    <span style={{ display: 'block', textAlign: 'center', wordBreak: 'break-word' }}>
                      {opt.label}
                    </span>
                    {opt.sub && (
                      <span
                        className="text-xs mt-0.5"
                        style={{
                          display: 'block',
                          textAlign: 'center',
                          color: 'var(--text-faint)',
                          wordBreak: 'break-word',
                          lineHeight: 1.3,
                        }}
                      >
                        {opt.sub}
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Count — sticky footer */}
          <div
            className="px-3 py-1.5 text-xs text-center"
            style={{
              position: 'sticky', bottom: 0, zIndex: 1,
              borderTop: '1px solid var(--border-subtle)',
              background: 'var(--surface-1)',
              color: 'var(--text-faint)',
            }}
          >
            {filtered.length} of {options.length}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   Badge — semantic variants; palettes re-tuned for light via :root.light
   overrides in index.css on .badge-* classes. We keep the component-level
   tailwind palette here too because it's used for NON-status badges (default,
   info, purple, indigo). Those are acceptable in both themes because the
   /20 tinted bg is legible on both.
   ═══════════════════════════════════════════════════════════════════════════ */
export const Badge = ({ children, variant = 'default', size = 'sm' }) => {
  const variants = {
    default: 'bg-[var(--surface-3)] text-[var(--text-secondary)] border border-[var(--border-subtle)]',
    success: 'bg-green-500/20 text-green-400 border border-green-500/30',
    warning: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
    danger:  'bg-red-500/20 text-red-400 border border-red-500/30',
    info:    'bg-blue-500/20 text-blue-400 border border-blue-500/30',
    purple:  'bg-purple-500/20 text-purple-400 border border-purple-500/30',
    indigo:  'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30',
    amber:   'bg-amber-500/20 text-amber-400 border border-amber-500/30',
  };
  const sizes = { sm: 'text-xs px-2 py-0.5', md: 'text-sm px-2.5 py-1' };
  return (
    <span className={`inline-flex items-center rounded-full font-medium ${variants[variant]} ${sizes[size]}`}>
      {children}
    </span>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   StatCard — gradient tints work on both themes but we drop the heavy
   saturation in light mode via the `data-variant` attribute + CSS hook.
   ═══════════════════════════════════════════════════════════════════════════ */
export const StatCard = ({ title, value, icon, color = 'indigo', change }) => {
  const tintByColor = {
    indigo: { from: 'rgba(99,102,241,0.20)', to: 'rgba(99,102,241,0.06)',  border: 'rgba(99,102,241,0.30)'  },
    green:  { from: 'rgba(16,185,129,0.20)', to: 'rgba(16,185,129,0.06)',  border: 'rgba(16,185,129,0.30)'  },
    blue:   { from: 'rgba(59,130,246,0.20)', to: 'rgba(59,130,246,0.06)',  border: 'rgba(59,130,246,0.30)'  },
    purple: { from: 'rgba(139,92,246,0.20)', to: 'rgba(139,92,246,0.06)',  border: 'rgba(139,92,246,0.30)'  },
    orange: { from: 'rgba(249,115,22,0.20)', to: 'rgba(249,115,22,0.06)',  border: 'rgba(249,115,22,0.30)'  },
  };
  const tint = tintByColor[color] || tintByColor.indigo;
  return (
    <div
      className="rounded-xl p-5 transition-all"
      style={{
        background: `linear-gradient(135deg, ${tint.from} 0%, ${tint.to} 100%)`,
        border: `1px solid ${tint.border}`,
        boxShadow: 'var(--shadow-card)',
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-2xl">{icon}</span>
        {change != null && (
          <span className={`text-xs font-medium ${change > 0 ? 'text-green-400' : 'text-red-400'}`}>
            {change > 0 ? '↑' : '↓'} {Math.abs(change)}%
          </span>
        )}
      </div>
      <div className="text-3xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{value}</div>
      <div className="text-sm" style={{ color: 'var(--text-tertiary)' }}>{title}</div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   Button — brand colors stay identical both themes; secondary/ghost now use
   tokens so they look right on white surfaces.
   ═══════════════════════════════════════════════════════════════════════════ */
export const Button = ({
  children, variant = 'primary', size = 'md',
  disabled = false, loading = false, loadingText,
  onClick, type = 'button', className = '',
  tooltip, iconOnly = false, style = {}, ...rest
}) => {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed]  = useState(false);
  const [spinner, setSpinner]  = useState(false);

  // Show spinner with a tiny delay so fast responses don't flash it
  useEffect(() => {
    let t;
    if (loading) { t = setTimeout(() => setSpinner(true), 80); }
    else { setSpinner(false); }
    return () => clearTimeout(t);
  }, [loading]);

  const isDisabled = disabled || loading;

  /* ── variant palette ──────────────────────────────────────────────── */
  const palette = {
    primary:   {
      bg:      'linear-gradient(135deg,#6366f1 0%,#8b5cf6 100%)',
      bgHover: 'linear-gradient(135deg,#4f46e5 0%,#7c3aed 100%)',
      bgPress: 'linear-gradient(135deg,#4338ca 0%,#6d28d9 100%)',
      color:   '#fff',
      shadow:  '0 4px 14px rgba(99,102,241,0.35)',
      shadowH: '0 6px 20px rgba(99,102,241,0.5)',
      border:  'none',
    },
    secondary: {
      bg:      'var(--bg-raised)',
      bgHover: 'var(--hover-bg)',
      bgPress: 'var(--bg-card)',
      color:   'var(--text-primary)',
      shadow:  'none',
      shadowH: '0 2px 8px rgba(0,0,0,0.1)',
      border:  '1px solid var(--border-subtle)',
    },
    danger: {
      bg:      '#dc2626',
      bgHover: '#b91c1c',
      bgPress: '#991b1b',
      color:   '#fff',
      shadow:  '0 4px 14px rgba(220,38,38,0.3)',
      shadowH: '0 6px 20px rgba(220,38,38,0.45)',
      border:  'none',
    },
    success: {
      bg:      '#16a34a',
      bgHover: '#15803d',
      bgPress: '#166534',
      color:   '#fff',
      shadow:  '0 4px 14px rgba(22,163,74,0.3)',
      shadowH: '0 6px 20px rgba(22,163,74,0.45)',
      border:  'none',
    },
    ghost: {
      bg:      'transparent',
      bgHover: 'var(--hover-bg)',
      bgPress: 'var(--bg-card)',
      color:   'var(--text-secondary)',
      shadow:  'none',
      shadowH: 'none',
      border:  'none',
    },
    outline: {
      bg:      'transparent',
      bgHover: 'var(--hover-bg)',
      bgPress: 'var(--bg-card)',
      color:   'var(--text-primary)',
      shadow:  'none',
      shadowH: '0 2px 8px rgba(0,0,0,0.08)',
      border:  '1px solid var(--border-muted)',
    },
    teal: {
      bg:      '#0f766e',
      bgHover: '#0d9488',
      bgPress: '#0f766e',
      color:   '#fff',
      shadow:  '0 4px 14px rgba(15,118,110,0.3)',
      shadowH: '0 6px 20px rgba(15,118,110,0.45)',
      border:  'none',
    },
    warning: {
      bg:      '#d97706',
      bgHover: '#b45309',
      bgPress: '#92400e',
      color:   '#fff',
      shadow:  '0 4px 14px rgba(217,119,6,0.3)',
      shadowH: '0 6px 20px rgba(217,119,6,0.45)',
      border:  'none',
    },
  }[variant] || {};

  /* ── size tokens ──────────────────────────────────────────────────── */
  const sizeStyle = {
    xs: { padding: iconOnly ? '4px' : '3px 8px',   fontSize: 11, borderRadius: 6,  minWidth: iconOnly ? 24 : undefined, height: iconOnly ? 24 : undefined },
    sm: { padding: iconOnly ? '6px' : '5px 12px',  fontSize: 12, borderRadius: 7,  minWidth: iconOnly ? 28 : undefined, height: iconOnly ? 28 : undefined },
    md: { padding: iconOnly ? '8px' : '7px 16px',  fontSize: 13, borderRadius: 8,  minWidth: iconOnly ? 34 : undefined, height: iconOnly ? 34 : undefined },
    lg: { padding: iconOnly ? '10px' : '9px 20px', fontSize: 14, borderRadius: 9,  minWidth: iconOnly ? 40 : undefined, height: iconOnly ? 40 : undefined },
    xl: { padding: iconOnly ? '12px' : '11px 24px',fontSize: 15, borderRadius: 10, minWidth: iconOnly ? 46 : undefined, height: iconOnly ? 46 : undefined },
  }[size] || { padding: '7px 16px', fontSize: 13, borderRadius: 8 };

  const activeP = palette;
  // Full-width / block buttons can carry long dynamic labels (candidate names,
  // client emails). Let those WRAP so the full label stays visible instead of
  // being clipped by the narrow mobile viewport. Inline (auto-width) buttons keep
  // nowrap so they stay compact and size to their text — desktop is unaffected.
  const isBlock = style?.width === '100%' || (className || '').split(/\s+/).includes('w-full');
  const bg = isDisabled
    ? activeP.bg
    : pressed ? activeP.bgPress
    : hovered ? activeP.bgHover
    : activeP.bg;
  const shadow = isDisabled ? 'none' : hovered && !pressed ? activeP.shadowH : activeP.shadow;
  const transform = isDisabled ? 'none' : pressed ? 'translateY(0) scale(0.97)' : hovered ? 'translateY(-1px)' : 'none';

  const btn = (
    <button
      type={type}
      disabled={isDisabled}
      onClick={onClick}
      onMouseEnter={() => { if (!isDisabled) setHovered(true); }}
      onMouseLeave={() => { setHovered(false); setPressed(false); }}
      onMouseDown={() => { if (!isDisabled) setPressed(true); }}
      onMouseUp={() => setPressed(false)}
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: iconOnly ? 0 : 6,
        fontWeight: 500,
        fontFamily: 'inherit',
        whiteSpace: isBlock ? 'normal' : 'nowrap',
        wordBreak: isBlock ? 'break-word' : undefined,
        textAlign: 'center',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.55 : 1,
        transition: 'background 0.18s ease, box-shadow 0.18s ease, transform 0.15s ease, opacity 0.15s ease',
        background: bg,
        color: activeP.color,
        boxShadow: shadow,
        border: activeP.border || 'none',
        transform,
        ...sizeStyle,
        ...style,
      }}
      {...rest}
    >
      {spinner && (
        <span style={{
          display: 'inline-block',
          width: sizeStyle.fontSize,
          height: sizeStyle.fontSize,
          border: '2px solid currentColor',
          borderTopColor: 'transparent',
          borderRadius: '50%',
          animation: 'btn-spin 0.65s linear infinite',
          flexShrink: 0,
        }} />
      )}
      {spinner && loadingText ? loadingText : children}
    </button>
  );

  if (!tooltip) return btn;

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      {btn}
      <span style={{
        position: 'absolute',
        bottom: 'calc(100% + 6px)',
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'rgba(15,15,15,0.9)',
        color: '#fff',
        fontSize: 11,
        padding: '4px 8px',
        borderRadius: 5,
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        opacity: hovered ? 1 : 0,
        transition: 'opacity 0.15s ease',
        zIndex: 9999,
      }}>
        {tooltip}
        <span style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          borderWidth: '4px 4px 0',
          borderStyle: 'solid',
          borderColor: 'rgba(15,15,15,0.9) transparent transparent',
        }} />
      </span>
    </span>
  );
};

export const IconButton = ({ children, tooltip, variant = 'ghost', size = 'sm', ...rest }) => (
  <Button variant={variant} size={size} iconOnly tooltip={tooltip} {...rest}>{children}</Button>
);

/* ═══════════════════════════════════════════════════════════════════════════
   Input / Select / Textarea — use .input-premium utility for shared themed
   field chrome; errors still red in both modes.
   ═══════════════════════════════════════════════════════════════════════════ */
const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);

export const Input = ({ label, error: externalError, className = '', type, onChange, ...props }) => {
  const [emailErr, setEmailErr] = useState('');

  const handleChange = (e) => {
    if (type === 'email') {
      const v = e.target.value;
      if (!v) setEmailErr('');
      else if (!v.includes('@')) setEmailErr('Email must include @');
      else if (!isValidEmail(v)) setEmailErr('Enter a valid email (e.g. user@domain.com)');
      else setEmailErr('');
    }
    onChange?.(e);
  };

  const error = externalError || emailErr;
  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
          {label}
        </label>
      )}
      <input
        type={type}
        className="input-premium w-full rounded-lg px-3 py-2.5 text-sm focus:outline-none transition-all"
        style={error ? { borderColor: '#ef4444' } : undefined}
        onChange={handleChange}
        {...props}
      />
      {error && <p className="mt-1 text-xs" style={{ color: '#ef4444' }}>{error}</p>}
    </div>
  );
};

export const Select = ({ label, error, children, className = '', ...props }) => (
  <div className={className}>
    {label && (
      <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
        {label}
      </label>
    )}
    <select
      className="input-premium w-full rounded-lg px-3 py-2.5 text-sm focus:outline-none"
      style={{ background: 'var(--input-bg)', color: 'var(--text-primary)', ...(error ? { borderColor: '#ef4444' } : {}) }}
      {...props}
    >
      {children}
    </select>
    {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
  </div>
);

export const Textarea = ({ label, error, className = '', ...props }) => (
  <div className={className}>
    {label && (
      <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
        {label}
      </label>
    )}
    <textarea
      className="input-premium w-full rounded-lg px-3 py-2.5 text-sm focus:outline-none transition-all resize-y"
      rows={4}
      style={error ? { borderColor: '#ef4444' } : undefined}
      {...props}
    />
    {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   Card
   ═══════════════════════════════════════════════════════════════════════════ */
export const Card = ({ children, className = '', title, action }) => (
  <div
    className={`rounded-xl ${className}`}
    style={{
      background: 'var(--surface-1)',
      border: '1px solid var(--border-subtle)',
      boxShadow: 'var(--shadow-card)',
    }}
  >
    {(title || action) && (
      <div
        className="flex items-center justify-between px-6 py-4"
        style={{ borderBottom: '1px solid var(--border-subtle)' }}
      >
        {title && (
          <h2 className="font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</h2>
        )}
        {action}
      </div>
    )}
    <div className="p-6">{children}</div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   Modal
   ═══════════════════════════════════════════════════════════════════════════ */
export const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  useLayoutEffect(() => {
    if (isOpen) {
      document.documentElement.classList.add('scroll-locked');
      return () => { document.documentElement.classList.remove('scroll-locked'); };
    }
  }, [isOpen]);

  if (!isOpen) return null;
  const sizeMap = { sm: 448, md: 576, lg: 896, xl: 1152 };
  const maxW = sizeMap[size] || 576;
  const modal = (
    <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      {/* Backdrop */}
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} onClick={onClose} />
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: maxW,
          maxHeight: '90vh',
          overflowY: 'auto',
          background: 'var(--surface-1)',
          border: '1px solid var(--border-muted)',
          borderRadius: 16,
          boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
        }}
        className="custom-scroll"
      >
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            padding: '16px 24px', position: 'sticky', top: 0, zIndex: 10,
            borderBottom: '1px solid var(--border-subtle)',
            background: 'var(--surface-1)',
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2 className="break-words" style={{ margin: 0, fontSize: 17, fontWeight: 600, color: 'var(--text-primary)', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{title}</h2>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 20, lineHeight: 1, cursor: 'pointer', color: 'var(--text-tertiary)', padding: '2px 4px', flexShrink: 0 }}
            onMouseEnter={e => (e.currentTarget.style.color = 'var(--text-primary)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-tertiary)')}
          >
            ✕
          </button>
        </div>
        <div className="p-4 sm:p-6">{children}</div>
      </div>
    </div>
  );
  return createPortal(modal, document.body);
};

/* ═══════════════════════════════════════════════════════════════════════════
   ConfirmModal  — replaces window.confirm() with a styled in-app dialog
   ═══════════════════════════════════════════════════════════════════════════ */
export const ConfirmModal = ({ isOpen, onConfirm, onCancel, title = 'Are you sure?', message, confirmLabel = 'Delete', danger = true }) => {
  useLayoutEffect(() => {
    if (isOpen) {
      document.documentElement.classList.add('scroll-locked');
      return () => { document.documentElement.classList.remove('scroll-locked'); };
    }
  }, [isOpen]);

  if (!isOpen) return null;
  const modal = (
    <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)' }} onClick={onCancel} />
      <div className="custom-scroll" style={{
        position: 'relative', background: 'var(--surface-1)', border: '1px solid var(--border-muted)',
        borderRadius: 16, padding: '28px 28px 24px', width: '100%', maxWidth: 400,
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
      }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: danger ? 'rgba(239,68,68,0.15)' : 'rgba(99,102,241,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, marginBottom: 16 }}>
          {danger ? '🗑️' : '⚠️'}
        </div>
        <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</h3>
        {message && <p style={{ margin: '0 0 24px', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{message}</p>}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'flex-end', marginTop: message ? 0 : 24 }}>
          <button onClick={onCancel} style={{
            padding: '9px 20px', borderRadius: 8, border: '1.5px solid var(--border-muted)',
            background: 'transparent', color: 'var(--text-primary)', fontSize: 14, fontWeight: 500, cursor: 'pointer',
          }}>Cancel</button>
          {/* Both fills are literals, deliberately. The label is a hardcoded
              white, so the background MUST be guaranteed opaque — routing it
              through a custom property once made the non-danger button render
              white-on-nothing (i.e. invisible) because --accent was undefined.
              Colour pairs that must stay in sync belong in the same place. */}
          <button onClick={onConfirm} style={{
            padding: '9px 20px', borderRadius: 8, border: 'none',
            background: danger ? '#ef4444' : '#6366f1', color: '#fff',
            fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
  return createPortal(modal, document.body);
};

/* ═══════════════════════════════════════════════════════════════════════════
   EmptyState
   ═══════════════════════════════════════════════════════════════════════════ */
export const EmptyState = ({ icon = '📭', title, message, action }) => (
  <div className="text-center py-16 px-4">
    <div className="text-5xl mb-4">{icon}</div>
    <h3 className="font-semibold text-lg mb-2 break-words max-w-md mx-auto" style={{ color: 'var(--text-primary)' }}>{title}</h3>
    <p className="text-sm mb-4 break-words max-w-md mx-auto" style={{ color: 'var(--text-tertiary)' }}>{message}</p>
    {action}
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   Pagination
   ═══════════════════════════════════════════════════════════════════════════ */
export const Pagination = ({ page, pages, onPage }) => {
  if (pages <= 1) return null;

  const go = (p) => { if (p >= 1 && p <= pages && p !== page) onPage(p); };

  const cellStyle = (active) => ({
    background: active ? 'var(--brand-primary)' : 'var(--surface-3)',
    color: active ? '#fff' : 'var(--text-secondary)',
    border: active ? 'none' : '1px solid var(--border-subtle)',
  });

  // Compact list: always show the first + last page and a small window around
  // the current page, with an ellipsis (…) for gaps — e.g. "1 … 8 9 10 … 33".
  // The current page is always visible; no scrolling needed.
  const buildItems = () => {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    let start = Math.max(2, page - 1);
    let end   = Math.min(pages - 1, page + 1);
    if (page <= 3)          { start = 2;         end = 4; }
    if (page >= pages - 2)  { start = pages - 3; end = pages - 1; }
    const items = [1];
    if (start > 2) items.push('left-gap');
    for (let i = start; i <= end; i++) items.push(i);
    if (end < pages - 1) items.push('right-gap');
    items.push(pages);
    return items;
  };

  return (
    <div className="flex items-center justify-center gap-1.5 sm:gap-2 mt-6 flex-wrap">
      <button
        disabled={page <= 1}
        onClick={() => go(page - 1)}
        className="px-2.5 sm:px-3 py-1.5 text-sm rounded-lg flex-shrink-0 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        style={cellStyle(false)}
        aria-label="Previous page"
      >
        ← <span className="hidden sm:inline">Prev</span>
      </button>

      {buildItems().map((it, i) =>
        typeof it === 'number' ? (
          <button
            key={it}
            onClick={() => go(it)}
            aria-current={it === page ? 'page' : undefined}
            className="min-w-[34px] sm:min-w-[36px] h-9 px-1.5 sm:px-2 text-sm rounded-lg flex-shrink-0 transition-all"
            style={cellStyle(it === page)}
          >
            {it}
          </button>
        ) : (
          <span key={`${it}-${i}`} className="w-6 sm:w-7 h-9 flex items-center justify-center text-sm select-none flex-shrink-0"
            style={{ color: 'var(--text-muted)' }}>…</span>
        )
      )}

      <button
        disabled={page >= pages}
        onClick={() => go(page + 1)}
        className="px-2.5 sm:px-3 py-1.5 text-sm rounded-lg flex-shrink-0 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        style={cellStyle(false)}
        aria-label="Next page"
      >
        <span className="hidden sm:inline">Next</span> →
      </button>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   Spinner
   ═══════════════════════════════════════════════════════════════════════════ */
export const Spinner = ({ size = 'md' }) => {
  const sizes = { sm: 'w-5 h-5', md: 'w-8 h-8', lg: 'w-12 h-12' };
  return (
    <div
      className={`${sizes[size]} border-2 border-t-transparent rounded-full animate-spin mx-auto`}
      style={{ borderColor: 'var(--brand-primary)', borderTopColor: 'transparent' }}
    />
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   statusVariant — maps status/priority strings to Badge variant names
   ═══════════════════════════════════════════════════════════════════════════ */
export const statusVariant = (value) => {
  const map = {
    Open:        'success',
    Closed:      'danger',
    'On Hold':   'warning',
    Low:         'default',
    Medium:      'info',
    High:        'warning',
    Urgent:      'danger',
    Applied:     'default',
    Shortlisted: 'warning',
    Interview:   'info',
    Hired:       'success',
    Rejected:    'danger',
  };
  return map[value] || 'default';
};